import { Component, OnInit } from "@angular/core";
import { Chart } from "chart.js";
import { FranchiseService } from "src/app/core/services/api/franchise.service";
import { HelpDeskService } from "src/app/core/services/api/help-desk.service";
import { PopupService } from "src/app/core/services/popup/popup.service";

@Component({
  selector: "app-franchise-dashboard",
  templateUrl: "./franchise-dashboard.component.html",
  styleUrls: ["./franchise-dashboard.component.scss"],
})
export class FranchiseDashboardComponent implements OnInit {
  sanctionedLimitAmount: any;
  availableLimitAmount: string;

  constructor(
    private franchiseService: FranchiseService,
    private popupService: PopupService,
    private helpDeskService: HelpDeskService,
  ) { }

  fromDate: string;
  toDate: string;

  billedChart: any;
  cnChart: any;
  categoryChart: any;

  totalBilled = 4280968;
  totalCN = 169166;

  paginatedData = [];
  commissionDetails = [];
  categoryDetails = [];
  categoryWiseCommissionDetails = [];
  categoryWiseCNCommissionDetails = [];

  page = 1;
  pageSize = 9;

  startRow = 1;
  endRow = 9;

  retailerCode = null;
  userData = null;
  totalCommissionAmount: any;
  totalGmvCategorywise: number = 0;
  gmvTotal: number = 0;
  creditNoteTotal: number = 0;
  childRetailersCount: any;
  creditCollectionAmount: any;
  totalDueAmount: any;
  childRetailerDetails: any;

  ngOnInit() {
    console.log("ngOnInit called");
    const userData = JSON.parse(localStorage.getItem("userData"));
    this.retailerCode = userData?.user?.retailerCode;
    this.userData = userData;

    this.setCurrentMonthDates();
    this.loadDashboardData();
  }

  loadDashboardData() {
    this.getCommisionDetails();
    this.getCategoryWiseDetails();
    this.getGMVTotalValue();
    this.getCreditNoteValue();
    this.getCategoryWiseCNCommissionDetails();
    this.getCategoryWiseCommissionDetails();
    this.getGMVTotalChartValue();
    this.getGMVTotalCNChartValue();
    this.getCreditCollectionValue();
    this.getTotalChildRetailersCount();
    this.getTotalDueAmount();
    this.getChildRetailerDetails();
    this.getSanctionedLimitValue();
    this.getAvailableLimitValue();
  }

  setCurrentMonthDates() {
    const today = new Date();

    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    this.fromDate = this.formatDate(firstDay);
    this.toDate = this.formatDate(today);
  }

  ngAfterViewInit() {
    this.applyFilter();
  }

  prepareCategoryData(response: any[]) {
    const colorMap: any = {
      GENERIC: "#5c62a3",
      OTHERS: "#e39c63",
      "HIGH MARGIN": "#f2c94c",
    };

    this.categoryDetails = response.map((item) => ({
      ...item,
      color: colorMap[item.category],
    }));

    this.totalGmvCategorywise = this.categoryDetails.reduce(
      (sum, item) => sum + item.gmv,
      0,
    );

    this.createCategoryChart();
  }

  getCategoryWiseDetails() {
    const params = {
      retailerCode: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getCategoryWiseDetails(params).subscribe(
      (response) => {
        this.categoryDetails = response;

        this.prepareCategoryData(response);
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getCategoryWiseCommissionDetails() {
    const params = {
      retailerCode: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getCategoryWiseCommissionDetails(params).subscribe(
      (response) => {
        this.categoryWiseCommissionDetails = response;
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getChildRetailerDetails() {
    const params = {
      franchiseCode: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getChildRetailerDetails(params).subscribe(
      (response) => {
        this.childRetailerDetails = response;
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getCategoryWiseCNCommissionDetails() {
    const params = {
      retailerCode: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getCategoryWiseCNCommissionDetails(params).subscribe(
      (response) => {
        this.categoryWiseCNCommissionDetails = response;
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getGMVTotalValue() {
    const params = {
      retailerCode: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getGMVTotalValue(params).subscribe(
      (response) => {
        this.gmvTotal = response.gmv;
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getTotalChildRetailersCount() {
    const params = {
      franchiseRetailerCode: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getTotalChildRetailersCount(params).subscribe(
      (response) => {
        this.childRetailersCount = response;
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getCreditCollectionValue() {
    const params = {
      retailerCode: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getCreditCollectionValue(params).subscribe(
      (response) => {
        this.creditCollectionAmount = String(
          response?.[0]?.totalCreditCollectionAmount ?? 0,
        );
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getSanctionedLimitValue() {
    const params = {
      franchiseCode: this.retailerCode,
    };
    this.franchiseService.getSanctionedLimitValue(params).subscribe(
      (response) => {
        this.sanctionedLimitAmount = response;
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getAvailableLimitValue() {
    const params = {
      franchiseCode: this.retailerCode,
    };
    this.franchiseService.getAvailableLimitValue(params).subscribe(
      (response) => {
        this.availableLimitAmount = response;
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getTotalDueAmount() {
    const params = {
      retailerCode: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getTotalDueAmount(params).subscribe(
      (response) => {
        this.totalDueAmount = String(response?.[0]?.totalDue ?? 0);
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getGMVTotalChartValue() {
    const params = {
      retailerCodes: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getGMVTotalChartValue(params).subscribe(
      (response) => {
        this.prepareBilledChartData(response);
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getGMVTotalCNChartValue() {
    const params = {
      retailerCode: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getGMVTotalCNChartValue(params).subscribe(
      (response) => {
        this.prepareCnChartData(response);
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getCreditNoteValue() {
    const params = {
      retailerCodes: this.retailerCode,
      fromDate: this.fromDate,
      toDate: this.toDate,
    };
    this.franchiseService.getCreditNoteValue(params).subscribe(
      (response) => {
        this.creditNoteTotal = response;
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  getCommisionDetails() {
    const params = {
      code: this.retailerCode,
      isDistributor: true,
    };
    this.franchiseService.getCommisionDetails(params).subscribe(
      (response) => {
        console.log(response);
        console.log(response.commissionSummaryList);
        this.commissionDetails = response.commissionSummaryList;
        this.totalCommissionAmount = response.totalAmount;
      },
      (error) => {
        this.popupService.closeProgress();
        this.popupService.openError(error.error.error.error);
      },
    );
  }

  createCategoryChart() {
    const canvas: any = document.getElementById("categoryChart");
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    const labels = this.categoryDetails.map((x) => x.category);
    const data = this.categoryDetails.map((x) => x.gmv);
    const colors = this.categoryDetails.map((x) => x.color);

    this.categoryChart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: labels,
        datasets: [
          {
            data: data,
            backgroundColor: colors,
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        cutoutPercentage: 70,
      },
    });
  }

  formatInputDate(date: Date): string {
    const year = date.getFullYear();
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const day = ("0" + date.getDate()).slice(-2);

    return `${year}-${month}-${day}`;
  }

  applyFilter() {
    this.loadDashboardData();
  }

  formatDate(date: Date): string {
    return date.toISOString().split("T")[0];
  }

  prepareBilledChartData(response: any[]) {
    const labels = response.map((item) =>
      new Date(item.invoiceDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      }),
    );

    const billedData = response.map((item) => item.gmv);

    this.createBilledChart(labels, billedData);
  }

  createBilledChart(labels: string[], billedData: number[]) {
    if (this.billedChart) {
      this.billedChart.destroy();
    }

    this.billedChart = new Chart("billedChart", {
      type: "bar",
      data: {
        labels: labels,
        datasets: [
          {
            label: "Billed",
            data: billedData,
            backgroundColor: "#4e73df",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
      },
    });
  }

  createCnChart(labels: string[], cnData: number[]) {
    if (this.cnChart) {
      this.cnChart.destroy();
    }

    this.cnChart = new Chart("cnChart", {
      type: "bar",
      data: {
        labels: labels,
        datasets: [
          {
            label: "CN",
            data: cnData,
            backgroundColor: "#e74a3b",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
      },
    });
  }

  prepareCnChartData(response: any[]) {
    const labels = response.map((item) =>
      new Date(item.invoiceDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      }),
    );

    const cnData = response.map((item) => item.cnValue);

    this.createCnChart(labels, cnData);
  }

  updateCharts(labels, billedData, cnData) {
    this.billedChart.data.labels = labels;
    this.billedChart.data.datasets[0].data = billedData;
    this.billedChart.update();

    this.cnChart.data.labels = labels;
    this.cnChart.data.datasets[0].data = cnData;
    this.cnChart.update();
  }
}
